/**
 * Player Service
 * Spotify playback control API
 */

import { getApiClient } from '../client';
import type {
  DevicesListResponse,
  SelectDeviceRequest,
  PlaybackStateResponse,
  StartPlaybackRequest,
  SeekRequest,
  SetVolumeRequest,
} from '../types';

export const playerService = {
  /**
   * List all available Spotify Connect devices
   */
  async getDevices(): Promise<DevicesListResponse> {
    return getApiClient().get<DevicesListResponse>('/player/devices');
  },

  /**
   * Transfer playback to a specific device
   */
  async selectDevice(request: SelectDeviceRequest): Promise<void> {
    return getApiClient().put<void>('/player/device', request);
  },

  /**
   * Get current playback state
   * Returns null if no active playback session (backend returns 404)
   */
  async getPlaybackState(): Promise<PlaybackStateResponse | null> {
    return getApiClient().get<PlaybackStateResponse>('/player/state');
  },

  /**
   * Start or resume playback
   * @param request - Optional playback options (context, tracks, offset)
   */
  async play(request?: StartPlaybackRequest): Promise<void> {
    return getApiClient().put<void>('/player/play', request);
  },

  /**
   * Pause playback
   */
  async pause(): Promise<void> {
    return getApiClient().put<void>('/player/pause');
  },

  /**
   * Skip to next track
   */
  async next(): Promise<void> {
    return getApiClient().post<void>('/player/next');
  },

  /**
   * Skip to previous track
   */
  async previous(): Promise<void> {
    return getApiClient().post<void>('/player/previous');
  },

  /**
   * Seek to position in currently playing track
   * @param request - Position in milliseconds and optional device ID
   */
  async seek(request: SeekRequest): Promise<void> {
    return getApiClient().put<void>('/player/seek', request);
  },

  /**
   * Set playback volume
   * @param request - Volume percentage (0-100) and optional device ID
   */
  async setVolume(request: SetVolumeRequest): Promise<void> {
    return getApiClient().put<void>('/player/volume', request);
  },
};
